// Import projects/services and sales from Excel or CSV. Columns are recognised by
// their headings (any order, many spellings); clients are matched by name or created.

import { parseAmount, parseDate, parseInvoiceImage, parseInvoicePdf, parseSpreadsheet, parseSpreadsheetFile } from "./importers";
import { suggestTag } from "./tags";
import { looksRecurring } from "./projectHealth";
import { num, projectLine, uid } from "./utils";

const FIELDS = {
  projects: [
    ["kind", /^(type|kind|item\s*type|project\s*\/\s*service)$/i],
    ["client", /^(client|customer|company|account|party|bill\s*to)(\s*name)?$/i],
    ["service", /^(service|services|package|scope|details|keywords|plan|service\s*details)$/i],
    ["name", /^(project|project\s*name|name|end[\s-]*client|item|description|title|brand|website|service\s*name|campaign)$/i],
    ["rate", /^(rate|price|fee|amount|monthly(\s*(fee|rate|amount))?|cost|value|retainer|unit\s*price)(\s*\(.*\))?$/i],
    ["qty", /^(qty|quantity|units?|hours?)$/i],
    ["billing", /^(billing|billing\s*type|frequency|cycle|recurring|term)$/i],
    ["status", /^(status|state|active)$/i],
    ["tag", /^(tag|category|service\s*type|department|group)$/i],
    ["taxRate", /^((gst|tax|vat)\s*(%|rate)?|gst\s*%|tax\s*%)$/i],
    ["hsn", /^(hsn|sac|hsn\s*\/\s*sac)(\s*code)?$/i],
    ["startDate", /^(start(\s*date)?|from|since)$/i],
    ["endDate", /^(end(\s*date)?|to|until)$/i],
    ["currency", /^(currency|curr\.?|ccy)$/i],
    ["notes", /^(notes?|remarks?|comments?)$/i],
  ],
  sales: [
    ["date", /^(date|sale\s*date|day|work\s*date|done\s*on)$/i],
    ["client", /^(client|customer|company|account|party)(\s*name)?$/i],
    ["project", /^(project|project\s*name|end[\s-]*client|website|brand)$/i],
    ["description", /^(description|details|item|work|task|service|sale|particulars|what)$/i],
    ["qty", /^(qty|quantity|units?|hours?|hrs)$/i],
    ["rate", /^(rate|price|unit\s*price|fee|cost)$/i],
    ["amount", /^(amount|total|value|line\s*total)(\s*\(.*\))?$/i],
    ["tag", /^(tag|category|service\s*type|type)$/i],
    ["notes", /^(notes?|remarks?|comments?)$/i],
  ],
};

const norm = (s) => String(s || "").toLowerCase().replace(/&/g, "and").replace(/\b(ltd|limited|llc|llp|inc|pvt|private|plc|gmbh|co|the)\b\.?/g, "").replace(/[^a-z0-9]/g, "");
const cellText = (v) => (v == null ? "" : v instanceof Date ? v.toISOString().slice(0, 10) : String(v).trim());

// Find the heading row and map columns → fields.
export function detectColumns(grid, kind) {
  const defs = FIELDS[kind];
  let best = null;
  for (let i = 0; i < Math.min(grid.length, 20); i++) {
    const map = {};
    (grid[i] || []).forEach((h, k) => {
      const t = cellText(h);
      if (!t || t.length > 40) return;
      const f = defs.find(([name, re]) => map[name] == null && re.test(t));
      if (f) map[f[0]] = k;
    });
    const score = Object.keys(map).length;
    if (score >= 2 && (!best || score > best.score)) best = { row: i, map, score };
  }
  return best;
}

// Returns either { tables } (a list with columns) or { invoices } (invoice documents –
// PDFs, scans, photos, or an invoice laid out in Excel). From an invoice only the
// client and its line items are used; bank details, taxes and totals are ignored.
export async function readImportFile(file, kind, { onProgress } = {}) {
  const name = file.name || "";
  const one = (d) => ({ invoices: d && (d.items?.length || d.billTo?.name) ? [{ ...d, source: name }] : [] });
  if (/\.pdf$/i.test(name)) return checkInvoices(one(await parseInvoicePdf(await file.arrayBuffer(), undefined, { fileName: name, onProgress })), kind);
  if (/\.(png|jpe?g|webp)$/i.test(name)) return checkInvoices(one(await parseInvoiceImage(file, { onProgress })), kind);
  const sheets = await parseSpreadsheet(file);
  const tables = [];
  for (const s of sheets) {
    const cols = detectColumns(s.grid, kind);
    if (!cols) continue;
    const get = (r, f) => (cols.map[f] == null ? "" : cellText(r[cols.map[f]]));
    const rows = s.grid.slice(cols.row + 1).filter((r) => r && r.some((c) => cellText(c)));
    tables.push({ sheet: s.name, map: cols.map, rows, get });
  }
  // A list needs a Client column (or a real project/date column); anything else that
  // reads as an invoice is imported as one.
  const isList = tables.some((t) => t.map.client != null || (kind === "sales" ? t.map.date != null && t.map.description != null : t.map.billing != null || t.map.kind != null));
  if (!isList) {
    const drafts = (await parseSpreadsheetFile(file)).filter((d) => d.billTo?.name && d.items?.length);
    if (drafts.length) return { invoices: drafts.map((d) => ({ ...d, source: d.source || name })) };
  }
  if (!tables.length) throw new Error(kind === "sales" ? "Couldn't find columns such as Date, Client, Description, Rate in this file." : "Couldn't find columns such as Client, Project / Service, Rate in this file.");
  return { tables };
}

function checkInvoices(res) {
  if (!res.invoices.length) throw new Error("Couldn't read an invoice in this file.");
  if (!res.invoices.some((d) => d.items?.length)) throw new Error("Found the invoice but no project / service lines in it.");
  return res;
}

// "Zenvy Wellness 20 Keywords" → { name: "Zenvy Wellness", service: "20 Keywords" }
export function splitLine(text) {
  // OCR sometimes reads a bullet or border as a leading "©", "|", "•"…
  const t = String(text || "").replace(/\s+/g, " ").trim().replace(/^[^\p{L}\p{N}(]+/u, "");
  const m = t.match(/^(.+?)\s+(\(?\d+\s*(?:keywords?|kws?|locations?|pages?|posts?|hours?|hrs|links?|backlinks?|blogs?|articles?|reels?|videos?)\)?(?:\s.*)?)$/i);
  return m && m[1].length > 1 ? { name: m[1].replace(/[\s,–-]+$/, ""), service: m[2] } : { name: t, service: "" };
}

const billToClient = (b = {}, d = {}) => ({
  currency: d.currency || "",
  email: b.email || "",
  phone: b.phone || "",
  address: b.address || "",
  area: b.area || "",
  city: b.city || "",
  state: b.state || "",
  postcode: b.postcode || "",
  country: b.country || "",
  gstin: b.gstin || "",
  contactPerson: b.contactPerson || "",
});

function clientResolver(store, batch) {
  const all = [...store.clients];
  const created = [];
  return {
    created,
    get(name, extra = {}) {
      const n = norm(name);
      if (!n) return null;
      let c = all.find((x) => norm(x.name) === n) || all.find((x) => norm(x.name).length > 3 && (norm(x.name).includes(n) || n.includes(norm(x.name))));
      if (!c) {
        c = { id: uid(), name: String(name).trim(), contactPerson: "", email: "", phone: "", address: "", city: "", state: "", country: "", gstin: "", taxMode: "", ...Object.fromEntries(Object.entries(extra).filter(([, v]) => v)), currency: extra.currency || "", notes: "Created by import", importBatch: batch };
        all.push(c);
        created.push(c);
      }
      return c;
    },
  };
}

const tagFor = (text, value, tags) => {
  const v = String(value || "").trim().toLowerCase();
  const hit = v && tags.find((t) => t.name.toLowerCase() === v || t.id === v || t.name.toLowerCase().startsWith(v));
  return hit ? hit.id : suggestTag(`${value || ""} ${text}`, tags) || "";
};

// taxFor(client) → tax rate to use for a client (0 for export / no-tax clients)
export function buildProjects(src, store, { batch, defaultClientId = "", taxFor }) {
  if (src.invoices) return projectsFromInvoices(src.invoices, store, { batch });
  const tables = src.tables || src;
  const clients = clientResolver(store, batch);
  const existing = new Set(store.projects.map((p) => `${p.clientId}|${norm(projectLine(p))}`));
  const projects = [];
  let skipped = 0;
  for (const t of tables) {
    for (const r of t.rows) {
      const g = (f) => t.get(r, f);
      let name = g("name");
      let service = g("service");
      if (!name && service) [name, service] = [service, ""];
      if (!name || /^(sub\s*)?total/i.test(name)) continue;
      const clientName = g("client");
      const client = clientName ? clients.get(clientName, { currency: g("currency").toUpperCase() }) : store.clients.find((c) => c.id === defaultClientId) || null;
      const kindRaw = g("kind").toLowerCase();
      const kind = /serv/.test(kindRaw) ? "service" : /proj/.test(kindRaw) ? "project" : client ? "project" : "service";
      const key = `${client?.id || ""}|${norm([name, service].filter(Boolean).join(" "))}`;
      const dup = existing.has(key);
      if (dup) skipped++;
      existing.add(key);
      const b = g("billing").toLowerCase();
      const st = g("status").toLowerCase();
      const taxCol = parseAmount(g("taxRate"));
      projects.push({
        id: uid(),
        kind,
        clientId: client?.id || "",
        name,
        service,
        billing: /hour/.test(b) ? "hourly" : /one|fixed|once|single|project/.test(b) ? "one-time" : "monthly",
        rate: parseAmount(g("rate")) ?? 0,
        qty: parseAmount(g("qty")) || 1,
        taxRate: taxCol != null ? (taxCol > 0 && taxCol < 1 ? taxCol * 100 : taxCol) : taxFor(client),
        hsn: g("hsn"),
        status: /pause|hold/.test(st) ? "paused" : /complet|done|closed|ended|inactive|stopp|no\b|false/.test(st) ? "completed" : "active",
        tag: tagFor(`${name} ${service}`, g("tag"), store.settings.tags),
        startDate: parseDate(g("startDate")),
        endDate: parseDate(g("endDate")),
        notes: g("notes"),
        importBatch: batch,
        ...(dup ? { _dup: true } : {}),
      });
    }
  }
  return { projects, clients: clients.created, skipped };
}

export function buildSales(src, store, { batch, defaultClientId = "", order = "dmy" }) {
  if (src.invoices) return salesFromInvoices(src.invoices, store, { batch });
  const tables = src.tables || src;
  const clients = clientResolver(store, batch);
  const sales = [];
  let skipped = 0;
  for (const t of tables) {
    for (const r of t.rows) {
      const g = (f) => t.get(r, f);
      const description = g("description") || g("project");
      if (!description || /^(sub\s*)?total/i.test(description)) continue;
      const client = g("client") ? clients.get(g("client")) : store.clients.find((c) => c.id === defaultClientId) || null;
      if (!client) {
        skipped++;
        continue;
      }
      const qty = parseAmount(g("qty")) || 1;
      const amount = parseAmount(g("amount"));
      let rate = parseAmount(g("rate"));
      if (rate == null && amount != null) rate = Math.round((amount / qty) * 100) / 100;
      const pname = norm(g("project"));
      const project = pname ? store.projects.find((p) => p.clientId === client.id && (norm(p.name) === pname || norm(projectLine(p)) === pname)) : null;
      sales.push({
        id: uid(),
        date: parseDate(g("date"), order) || new Date().toISOString().slice(0, 10),
        clientId: client.id,
        projectId: project?.id || "",
        description,
        qty,
        rate: num(rate),
        tag: tagFor(description, g("tag"), store.settings.tags) || project?.tag || "",
        notes: g("notes"),
        importBatch: batch,
      });
    }
  }
  return { sales, clients: clients.created, skipped };
}

// Tax as charged on the invoice itself (0 for export / no-tax invoices).
const invoiceTaxRate = (d) => {
  const tax = num(d.cgst) + num(d.sgst) + num(d.igst) + num(d.vat);
  const sub = num(d.subtotal) || (d.items || []).reduce((s, l) => s + num(l.qty) * num(l.rate), 0);
  if (!tax || !sub) return 0;
  const r = (tax / sub) * 100;
  return [0, 5, 12, 18, 20, 28].reduce((a, b) => (Math.abs(b - r) < Math.abs(a - r) ? b : a), 0);
};

// Each invoice line becomes a project for the invoice's client – monthly when it
// sounds like ongoing work (SEO keywords, ads, maintenance…), otherwise one-time.
function projectsFromInvoices(invoices, store, { batch }) {
  const clients = clientResolver(store, batch);
  const existing = new Set(store.projects.map((p) => `${p.clientId}|${norm(projectLine(p))}`));
  const projects = [];
  let skipped = 0;
  for (const d of invoices) {
    const client = d.billTo?.name ? clients.get(d.billTo.name, billToClient(d.billTo, d)) : null;
    for (const l of d.items || []) {
      const { name, service } = splitLine(l.description);
      if (!name) continue;
      const key = `${client?.id || ""}|${norm(projectLine({ name, service }))}`;
      const dup = existing.has(key);
      if (dup) skipped++;
      existing.add(key);
      const monthly = looksRecurring(l.description);
      projects.push({
        id: uid(),
        kind: client ? "project" : "service",
        clientId: client?.id || "",
        name,
        service,
        billing: monthly ? "monthly" : "one-time",
        rate: num(l.rate),
        qty: num(l.qty) || 1,
        taxRate: num(l.taxRate) || invoiceTaxRate(d),
        hsn: l.hsn || "",
        status: "active",
        tag: tagFor(l.description, "", store.settings.tags),
        // billed on this invoice → running since (at least) that month
        startDate: monthly && (d.periodMonth || d.date) ? `${(d.periodMonth || d.date).slice(0, 7)}-01` : "",
        endDate: "",
        notes: d.number ? `From invoice ${d.number}` : "",
        importBatch: batch,
        ...(dup ? { _dup: true } : {}),
      });
    }
  }
  return { projects, clients: clients.created, skipped, fromInvoices: invoices.length };
}

function salesFromInvoices(invoices, store, { batch }) {
  const clients = clientResolver(store, batch);
  const sales = [];
  let skipped = 0;
  for (const d of invoices) {
    const client = d.billTo?.name ? clients.get(d.billTo.name, billToClient(d.billTo, d)) : null;
    // already in the app as an invoice → the sales are recorded as billed by it
    const billed = d.number && store.invoices.find((i) => norm(i.number) === norm(d.number) && i.status !== "cancelled");
    for (const l of d.items || []) {
      if (!client) {
        skipped++;
        continue;
      }
      const pname = norm(l.description);
      const project = store.projects.find((p) => p.clientId === client.id && (norm(projectLine(p)) === pname || norm(p.name) === pname));
      sales.push({
        id: uid(),
        date: d.date || new Date().toISOString().slice(0, 10),
        clientId: client.id,
        projectId: project?.id || "",
        description: l.description,
        qty: num(l.qty) || 1,
        rate: num(l.rate),
        tag: tagFor(l.description, "", store.settings.tags) || project?.tag || "",
        notes: d.number ? `From invoice ${d.number}` : "",
        ...(billed ? { invoiceId: billed.id } : {}),
        importBatch: batch,
      });
    }
  }
  return { sales, clients: clients.created, skipped, fromInvoices: invoices.length };
}
